import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Mail, MapPin, BadgeCheck, Check, X, Briefcase, ExternalLink, Loader2 } from 'lucide-react';

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_LABEL = {
  viewed: { text: 'New invitation', cls: 'bg-secondary/10 text-secondary' },
  responded: { text: 'Accepted', cls: 'bg-emerald-100 text-emerald-700' },
  applied: { text: 'Applied', cls: 'bg-emerald-100 text-emerald-700' },
  declined: { text: 'Declined', cls: 'bg-gray-100 text-gray-500' },
};

const CandidateInvitations = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () => axios.get(`${API_URL}/my-invitations`, { headers: authHeaders() })
    .then((r) => setData(r.data)).catch(() => setData({ invitations: [], counts: {} }));

  useEffect(() => { document.title = 'My Invitations | INASW'; load(); }, []);

  const respond = async (inv, action) => {
    setBusy(inv.invitation_id);
    try {
      const r = await axios.post(`${API_URL}/invitations/${inv.invitation_id}/respond`, { action }, { headers: authHeaders() });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not respond'); } finally { setBusy(''); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center pt-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pt-24 pb-16" data-testid="candidate-invitations">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-3xl font-bold text-primary flex items-center gap-2"><Mail className="w-7 h-7 text-secondary" /> Employer Invitations</h1>
        <p className="text-gray-600 mt-1">Verified employers who featured a job invited you to apply. Your contact details stay private until you respond.</p>

        {data.invitations.length === 0 ? (
          <div className="bg-white rounded-2xl border p-10 text-center mt-6" data-testid="invitations-empty">
            <Mail className="w-10 h-10 text-gray-300 mx-auto" />
            <p className="text-gray-500 mt-3">No invitations yet. Make sure employer opportunities are turned on in your account settings so employers can reach you.</p>
            <Link to="/jobs"><Button variant="outline" className="mt-4">Browse jobs</Button></Link>
          </div>
        ) : (
          <div className="space-y-3 mt-6">
            {data.invitations.map((inv) => {
              const st = STATUS_LABEL[inv.status] || STATUS_LABEL.viewed;
              const pending = inv.status === 'viewed';
              return (
                <div key={inv.invitation_id} className="bg-white rounded-xl border p-5" data-testid={`invitation-${inv.invitation_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/jobs/${inv.job_id}`} className="font-serif text-lg font-bold text-primary hover:underline truncate">{inv.job_title}</Link>
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-semibold"><BadgeCheck className="w-3.5 h-3.5" /> Verified employer</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {inv.company_name}{inv.location ? <span className="ml-2 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{inv.location}</span> : null}
                        {inv.license_required ? <span className="ml-2 inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{inv.license_required}</span> : null}
                      </p>
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${st.cls}`} data-testid={`invitation-status-${inv.invitation_id}`}>{st.text}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Link to={`/jobs/${inv.job_id}`}><Button size="sm" variant="outline" data-testid={`invitation-view-${inv.invitation_id}`}><ExternalLink className="w-3.5 h-3.5 mr-1" /> View job</Button></Link>
                    {pending && (
                      <>
                        <Button size="sm" onClick={() => respond(inv, 'accept')} disabled={busy === inv.invitation_id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`invitation-accept-${inv.invitation_id}`}>
                          {busy === inv.invitation_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />} Accept &amp; show interest
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => respond(inv, 'decline')} disabled={busy === inv.invitation_id} className="text-gray-500" data-testid={`invitation-decline-${inv.invitation_id}`}>
                          <X className="w-3.5 h-3.5 mr-1" /> Decline
                        </Button>
                      </>
                    )}
                  </div>
                  {!inv.job_active && <p className="text-xs text-amber-600 mt-2">This position is no longer active.</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateInvitations;
